"use strict";
// Copyright © Veeam Software Group GmbH
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAdaptiveLayout = void 0;
const react_1 = require("react");
const helpers_1 = require("../../helpers");
const Toolbar_1 = require("./..");
const findStartIndex_1 = require("./helpers/findStartIndex");
const getOtherActionsControl_1 = require("./helpers/getOtherActionsControl");
const splitArrayByIndex_1 = require("./helpers/splitArrayByIndex");
const sumToIndex_1 = require("./helpers/sumToIndex");
const useAdaptiveLayout = (containerRef, itemsRefs, items) => {
    // Need to rerender if adaptiveItems changed
    const [adaptiveItems, setAdaptiveItems] = (0, react_1.useState)([...items]);
    const { width: containerWidth } = (0, helpers_1.useResizeObserver)(containerRef);
    // These refs contain information about all toolbar items sizes after the first component render.
    // We need them to calculate when we need to toggle item.
    // Calculation of indents needed because all toolbar items can have different left indent.
    const elementsWidthRef = (0, react_1.useRef)([]);
    const elementsLeftsRef = (0, react_1.useRef)([]);
    const elementsIndentRef = (0, react_1.useRef)([]);
    // Calculate width and indents
    (0, react_1.useLayoutEffect)(() => {
        var _a, _b, _c, _d;
        const containerLeft = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().left;
        if (containerLeft) {
            for (let index = 0; index < items.length; index += 1) {
                const elementRect = (_b = itemsRefs.current[index].current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
                const elementWidth = (_c = elementRect === null || elementRect === void 0 ? void 0 : elementRect.width) !== null && _c !== void 0 ? _c : 0;
                const elementLeft = (_d = elementRect === null || elementRect === void 0 ? void 0 : elementRect.left) !== null && _d !== void 0 ? _d : 0;
                elementsLeftsRef.current.push(Math.round(elementLeft - containerLeft));
                elementsWidthRef.current.push(Math.round(elementWidth));
                const prevItem = items[index - 1];
                const elementIndent = !prevItem
                    ? 0
                    : elementsLeftsRef.current[index] -
                        (elementsLeftsRef.current[index - 1] + elementsWidthRef.current[index - 1]);
                elementsIndentRef.current.push(elementIndent);
            }
            // we don't need count width of TOOLBAR_ITEM_TYPE.fillSpace, because it depends on screen size
            items.forEach((item, index) => {
                if (item.type === Toolbar_1.TOOLBAR_ITEM_TYPE.fillSpace) {
                    elementsWidthRef.current[index] = 0;
                    elementsIndentRef.current[index] = 0;
                }
            });
        }
    }, [containerRef, items, itemsRefs]);
    // Toggle items
    (0, react_1.useLayoutEffect)(() => {
        if (containerWidth) {
            // Calculate the size of 'Other actions' control, to be able to know when to show it
            const otherActionsIndex = items.length - 1;
            const otherActionsContolWidth = elementsWidthRef.current[otherActionsIndex] +
                elementsIndentRef.current[otherActionsIndex];
            // Remove 'Other size' from first items (we added before to be able to calculate its size via its Ref)
            const pureItems = items.filter((item) => item.id !== getOtherActionsControl_1.TOOLBAR_OTHER_ACTIONS_CONTROL_ID);
            const totalElementsWidth = (0, sumToIndex_1.sumToIndex)(elementsWidthRef.current, pureItems.length - 1) +
                (0, sumToIndex_1.sumToIndex)(elementsIndentRef.current, pureItems.length - 1);
            if (totalElementsWidth < containerWidth) {
                requestAnimationFrame(() => {
                    setAdaptiveItems(pureItems);
                });
                return;
            }
            // Need start index because we don't do adaptation for custom elements
            const startIndex = (0, findStartIndex_1.findStartIndex)(pureItems);
            for (let index = startIndex; index < pureItems.length; index += 1) {
                const itemsToIndexWidth = (0, sumToIndex_1.sumToIndex)(elementsWidthRef.current, index) +
                    (0, sumToIndex_1.sumToIndex)(elementsIndentRef.current, index);
                if (itemsToIndexWidth > containerWidth - otherActionsContolWidth) {
                    // if it is last item in toolbar, we hide 2 items, if not - only 1
                    const cutIndex = index === pureItems.length - 1 ? index - 1 : index;
                    const [visiblePart, hiddenPart] = (0, splitArrayByIndex_1.splitArrayByIndex)(pureItems, cutIndex);
                    const newItems = [
                        ...visiblePart,
                        (0, getOtherActionsControl_1.getOtherActionsControl)(hiddenPart.filter((item) => item.type !== Toolbar_1.TOOLBAR_ITEM_TYPE.fillSpace))
                    ];
                    requestAnimationFrame(() => {
                        setAdaptiveItems(newItems);
                    });
                    break;
                }
            }
        }
    }, [items, containerWidth]);
    return adaptiveItems;
};
exports.useAdaptiveLayout = useAdaptiveLayout;
//# sourceMappingURL=useAdaptiveLayout.js.map